/**
 * Hook for fee transaction submission logic
 * Handles updating fee transactions and syncing with transfer dialog
 */

import { useCallback } from "react";
import { FormEvent } from "react";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  increment,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Account } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { FeeEditFormData } from "../components/FeeEditDialog";

export interface UseFeeSubmissionParams {
  workspaceId: string;
  feeTransaction: TransactionWithDate;
  feeFormData: FeeEditFormData;
  accounts: Account[];
  defaultCurrency: string;
  isSubmittingFee: boolean;
  setIsSubmittingFee: (submitting: boolean) => void;
  setFeeEditDialogOpen: (open: boolean) => void;
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setTransferFormData?: (data: any) => void;
  setShowTransferFee?: (show: boolean) => void;
  setFormData?: (data: any) => void;
  setSnackbarElement: (element: { severity: string; message: string } | null) => void;
  setSnackbarOpen: (open: boolean) => void;
  saveTransactionHistory: (
    transactionId: string,
    oldTransaction: TransactionWithDate,
    newTransactionData: any
  ) => Promise<void>;
  setTransactionHasHistory: (updater: (prev: Set<string>) => Set<string>) => void;
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
  /** Called after successful fee update (e.g. for mobile to navigate back) */
  onSuccess?: () => void;
}

export interface UseFeeSubmissionResult {
  handleFeeSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

/**
 * Hook for fee transaction submission
 */
export const useFeeSubmission = (
  params: UseFeeSubmissionParams
): UseFeeSubmissionResult => {
  const {
    workspaceId,
    feeTransaction,
    feeFormData,
    accounts,
    defaultCurrency,
    isSubmittingFee,
    setIsSubmittingFee,
    setFeeEditDialogOpen,
    setEditingTransaction,
    setTransferFormData,
    setShowTransferFee,
    setFormData,
    setSnackbarElement,
    setSnackbarOpen,
    saveTransactionHistory,
    setTransactionHasHistory,
    getAccountName,
    onSuccess,
  } = params;

  const handleFeeSubmit = useCallback(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (isSubmittingFee) {
      return;
    }

    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) {
      return;
    }

    const feeAmount = parseFloat(feeFormData.feeAmount);
    if (isNaN(feeAmount) || feeAmount < 0) {
      alert("Please enter a valid fee amount");
      return;
    }

    if (!feeFormData.feeAccountId) {
      alert("Please select a fee account");
      return;
    }

    // DEMO MODE: Block all write operations
    if (isDemoMode()) {
      setSnackbarElement({
        severity: "info",
        message: "Demo mode: Fee cannot be updated. Please log in to update fees.",
      });
      setSnackbarOpen(true);
      setFeeEditDialogOpen(false);
      setEditingTransaction(null);
      return;
    }

    setIsSubmittingFee(true);

    try {
      // Ensure user is in workspace members subcollection
      if (auth.currentUser && workspaceId && !isBypassAuthEnabled()) {
        const userId = auth.currentUser.uid;
        const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
        const memberSnap = await getDoc(memberRef);
        
        if (!memberSnap.exists()) {
          const workspaceRef = doc(db, "workspaces", workspaceId);
          const workspaceSnap = await getDoc(workspaceRef);
          const workspaceData = workspaceSnap.data();
          const isOwner = workspaceSnap.exists() && workspaceData?.ownerId === userId;
          
          if (isOwner) {
            await updateDoc(memberRef, {
              role: "owner",
              joinedAt: serverTimestamp(),
            });
          } else {
            throw new Error("User is not a member of this workspace.");
          }
        }
      }

      const feeAccount = accounts.find((acc) => acc.id === feeFormData.feeAccountId);
      if (!feeAccount) {
        alert("Fee account not found");
        setIsSubmittingFee(false);
        return;
      }

      // Get old fee amount and account
      const oldFeeAmount = feeTransaction.amount || 0;
      const oldFeeAccountId = feeTransaction.accountId;

      // Use batch write for atomic updates
      const batch = writeBatch(db);

      // Update fee account balance
      // Reverse old fee effect and apply new fee
      if (oldFeeAccountId && oldFeeAccountId === feeAccount.id) {
        // Same account - adjust balance by difference
        const balanceChange = oldFeeAmount - feeAmount;
        if (balanceChange !== 0) {
          const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
          batch.update(feeAccountRef, { amount: increment(balanceChange) });
        }
      } else {
        // Different account or no old account - reverse old fee and apply new fee
        if (oldFeeAccountId) {
          const oldFeeAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldFeeAccountId);
          batch.update(oldFeeAccountRef, { amount: increment(oldFeeAmount) });
        }
        const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
        batch.update(feeAccountRef, { amount: increment(-feeAmount) });
      }

      // Update fee transaction
      const feeTransactionRef = doc(db, "workspaces", workspaceId, "transactions", feeTransaction.id);
      const feeTransactionData: any = {
        amount: feeAmount,
        currency: feeAccount.currency,
        accountId: feeAccount.id,
        accountName: feeAccount.accountName,
        amountInDefaultCurrency: feeAmount, // Same as amount since rate is 1
        updatedAt: serverTimestamp(),
      };

      batch.update(feeTransactionRef, feeTransactionData);

      await batch.commit();

      // Save transaction history
      await saveTransactionHistory(feeTransaction.id, feeTransaction, feeTransactionData);
      
      // Mark transaction as having history
      setTransactionHasHistory((prev) => new Set(prev).add(feeTransaction.id));

      // Sync with transfer dialog if it's open (transfer fee)
      if (setTransferFormData && feeTransaction.transferFeeFor) {
        setTransferFormData((prev: any) => ({
          ...prev,
          feeAmount: feeAmount > 0 ? feeAmount.toString() : undefined,
          feeAccountId: feeAccount.id,
        }));
        if (setShowTransferFee && feeAmount > 0) {
          setShowTransferFee(true);
        } else if (setShowTransferFee && feeAmount === 0) {
          setShowTransferFee(false);
        }
      }
      // Sync with expense form if open (expense fee)
      if (setFormData && (feeTransaction as any).expenseFeeFor) {
        setFormData((prev: any) => ({
          ...prev,
          feeAmount: feeAmount > 0 ? feeAmount.toString() : undefined,
          feeAccountId: feeAccount.id,
        }));
      }

      setFeeEditDialogOpen(false);
      setEditingTransaction(null);
      setSnackbarElement({
        severity: "success",
        message: "Fee updated successfully",
      });
      setSnackbarOpen(true);
      onSuccess?.();
    } catch (error) {
      console.error("Error updating fee:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      alert(`Failed to update fee: ${errorMessage}`);
    } finally {
      setIsSubmittingFee(false);
    }
  }, [
    isSubmittingFee,
    workspaceId,
    feeTransaction,
    feeFormData,
    accounts,
    defaultCurrency,
    setIsSubmittingFee,
    setFeeEditDialogOpen,
    setEditingTransaction,
    setTransferFormData,
    setShowTransferFee,
    setFormData,
    setSnackbarElement,
    setSnackbarOpen,
    saveTransactionHistory,
    setTransactionHasHistory,
    getAccountName,
  ]);

  return {
    handleFeeSubmit,
  };
};
